import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RefLink from '../shared/RefLink';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';
import { fetchJSON } from '../../utils/api';
import { detailLabel } from './constants';

// 「這一關在等誰簽核」的小卡。Engagement Progress 的
// Pending review chip 點下去就開這張，答案來自 GET /api/workflow/progress/
// waiting（後端 services/pendingReview——名單、打勾、法定人數都在那邊算好）。
//
// 為什麼是 portal ＋ fixed：chip 坐在卡片的 `table-scroll` 表格裡，一般的
// absolute 小卡會被那層裁掉半張。錨點用 chip 的 getBoundingClientRect，捲動或
// 改變視窗大小就直接收起來——追著錨點重算位置只會換來抖動的小卡。
//
// 名單只是唸出來給人看，**不是權限判斷**：誰真的簽得下去由各流程的 approve
// 端點把關，這裡少列或多列一個人都不會讓誰簽得成或簽不成。
const WIDTH = 288;
const MAX_PEOPLE = 8;

const PersonRow = ({ person }) => (
  <li className="flex flex-wrap items-center gap-1.5 text-xs text-slate-700">
    {person.approved
      ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
      : <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
    <span className={person.approved ? 'text-slate-400' : ''}>{person.name}</span>
    <span className="text-[10px] text-slate-400">{person.role}</span>
    {!person.hasAccount && <span className="text-[10px] text-amber-600">no account</span>}
  </li>
);

const Gate = ({ gate }) => {
  const shown = gate.people.slice(0, MAX_PEOPLE);
  const hidden = gate.people.length - shown.length;
  return (
    <div className={gate.open ? '' : 'opacity-60'}>
      <p className="text-[11px] font-semibold text-slate-400 uppercase mb-1">
        {gate.label}
        {!gate.open && <span className="normal-case font-normal"> · waiting for the previous stage</span>}
      </p>
      {gate.people.length === 0 ? (
        <p className="text-xs text-amber-700">Nobody is eligible.</p>
      ) : (
        <ul className="space-y-1">
          {shown.map((p) => <PersonRow key={`${p.name}-${p.role}`} person={p} />)}
          {hidden > 0 && <li className="text-[11px] text-slate-400">+{hidden} more</li>}
        </ul>
      )}
      {gate.note && <p className="text-[11px] text-slate-400 mt-1">{gate.note}</p>}
    </div>
  );
};

const PendingReviewPopover = ({ engagementId, stage, anchor, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const ref = useRef(null);
  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    let alive = true;
    fetchJSON(`/api/workflow/progress/waiting?engagementId=${engagementId}&stage=${stage}`)
      .then((res) => { if (alive) setData(res); })
      .catch((e) => { if (alive) setError(e.message); });
    return () => { alive = false; };
  }, [engagementId, stage]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [close]);

  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8));
  const top = anchor.bottom + 6;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      style={{ position: 'fixed', top, left, width: WIDTH }}
      className="z-50 rounded-lg border border-slate-200 bg-white shadow-lg p-3 space-y-2"
    >
      {!data && !error && (
        <p className="text-xs text-slate-500 flex items-center gap-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </p>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      {data && (
        <>
          <div className="flex items-center justify-between gap-2">
            <span className={detailLabel}>Waiting On</span>
            <RefLink
              to={data.request.href}
              onClick={close}
              className="text-xs font-mono text-blue-600 hover:underline"
            >
              {data.request.requestNo}
            </RefLink>
          </div>
          {data.gates.map((g) => <Gate key={g.key} gate={g} />)}
          {data.note && <p className="text-[11px] text-amber-700">{data.note}</p>}
        </>
      )}
    </div>,
    document.body
  );
};

/**
 * 把任何一個「在審」的章包成可點的觸發器（Progress 的六個
 * 關卡 chip、Progress／Overview／Management 三處的 Current stage 章都要點得
 * 開）。狀態各自帶著走——同時開兩張小卡不會壞，而且省掉每個用它的頁面都要
 * 自己管一份 openKey。
 *
 * **一定要 stopPropagation ＋ preventDefault**：Overview 的整列是一個
 * `<Link>`、Management 的表格列整列可點，少了這兩句，點「看等誰」會直接把人
 * 帶去別的頁面。
 */
export const PendingReviewTrigger = ({ engagementId, stage, children }) => {
  const [anchor, setAnchor] = useState(null);
  if (!engagementId || !stage) return children;
  return (
    <>
      <button
        type="button"
        title="Who is this waiting on?"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const r = e.currentTarget.getBoundingClientRect();
          setAnchor((cur) => (cur ? null : { left: r.left, bottom: r.bottom }));
        }}
        className="rounded focus:outline-none focus:ring-2 focus:ring-amber-300 hover:brightness-95"
      >
        {children}
      </button>
      {anchor && (
        <PendingReviewPopover
          engagementId={engagementId}
          stage={stage}
          anchor={anchor}
          onClose={() => setAnchor(null)}
        />
      )}
    </>
  );
};

export default PendingReviewPopover;
